import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'

import useUser from '../../services/useUser'
import { getDpaAmendmentStatus, acceptDpaAmendment } from '../../services/firebase/dpaAmendment'

/**
 * #2287 (spec 2110 §29; Shai's decision on #2176, 20.9.2026) — the first-activation
 * screen for the DPA amendments: the document plus a checkbox, shown to the admin
 * of a NEW employer (opened from 20.9.2026) before they use the service.
 *
 * The client decides nothing about who is asked — `getDpaAmendmentStatus` does,
 * from the caller's verified claims and the employer's `createdAt` — so an
 * existing customer's admin simply gets `required: false` and never sees this.
 * If no amendment version is published the server answers `required: false` and
 * this renders nothing.
 *
 * Declared failure direction: while the status is loading, and when the status
 * call fails, the app stays usable. The acknowledgement is the thing that must be
 * recorded, but taking payroll down because a check failed would be the worse
 * error (spec §21.6: a missing document blocks only what depends on it). A failure
 * is logged, and the server logs the same case.
 *
 * Renders as a modal that cannot be dismissed — the only ways out are the
 * checkbox + confirm button.
 */
export default function DpaAmendmentGate() {
  const user = useUser()
  const role = user?.customClaims?.role
  const employer = user?.customClaims?.employer
  const isEmployerAdmin = role === 'employerAdmin' && Boolean(employer)

  const [pending, setPending] = useState(null) // { version, text }
  const [checked, setChecked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isEmployerAdmin) return undefined
    let cancelled = false
    getDpaAmendmentStatus()
      .then(status => {
        if (!cancelled && status?.required && status.version && status.text) {
          setPending({ version: status.version, text: status.text })
        }
      })
      .catch(err => {
        console.error('DPA amendment status check failed:', err)
      })
    return () => {
      cancelled = true
    }
  }, [isEmployerAdmin, employer])

  if (!isEmployerAdmin || !pending) return null

  const handleConfirm = async () => {
    setSaving(true)
    setError(null)
    try {
      await acceptDpaAmendment({ version: pending.version })
      setPending(null)
    } catch (err) {
      console.error('DPA amendment acceptance failed:', err)
      setError('לא הצלחנו לתעד את האישור. נסו שוב.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open fullWidth maxWidth="md" scroll="paper" onClose={() => {}} disableEscapeKeyDown>
      <DialogTitle>תיקוני הסכם עיבוד המידע (DPA)</DialogTitle>
      <DialogContent dividers>
        <Box
          data-testid="dpa-amendment-text"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {pending.text}
        </Box>
        <FormControlLabel
          sx={{ mt: 2, display: 'flex' }}
          control={
            <Checkbox
              checked={checked}
              onChange={event => setChecked(event.target.checked)}
              disabled={saving}
            />
          }
          label="קראתי ואני מאשר/ת את תיקוני ההסכם בשם המעסיק"
        />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleConfirm} disabled={!checked || saving}>
          אישור והמשך
        </Button>
      </DialogActions>
    </Dialog>
  )
}
